import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  LinearProgress,
  Chip,
  useTheme,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocationOn,
  Receipt,
  ShoppingCart,
  Assessment,
} from '@mui/icons-material';
import { ProjectMarginDTO } from '../../types/expense.types';

interface ProjectMarginsViewProps {
  projects: ProjectMarginDTO[];
  loading?: boolean;
}

/**
 * Composant pour afficher la vue financière par projet
 */
export const ProjectMarginsView: React.FC<ProjectMarginsViewProps> = ({
  projects,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)} %`;
  };

  const getMarginColor = (tauxMarge: number) => {
    if (tauxMarge >= 50) return '#10B981'; // Vert
    if (tauxMarge >= 30) return '#F59E0B'; // Orange
    return '#EF4444'; // Rouge
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Aucun projet avec données financières
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Les projets apparaîtront ici une fois qu'ils auront des factures ou des dépenses
        </Typography>
      </Paper>
    );
  }

  // Calculer les statistiques globales
  const totalFacture = projects.reduce((sum, p) => sum + p.totalFacture, 0);
  const totalDepenses = projects.reduce((sum, p) => sum + p.totalDepenses, 0);
  const margeBruteGlobale = totalFacture - totalDepenses;
  const tauxMargeGlobal = totalFacture > 0 ? (margeBruteGlobale / totalFacture) * 100 : 0;

  /**
   * Carte KPI pour un projet
   */
  const ProjectCard: React.FC<{ project: ProjectMarginDTO }> = ({ project }) => {
    const marginColor = getMarginColor(project.tauxMarge);

    return (
      <Card
        elevation={2}
        sx={{
          height: '100%',
          borderLeft: `4px solid ${marginColor}`,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[6],
          },
        }}
      >
        <CardContent>
          {/* En-tête du projet */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {project.projetTitre}
            </Typography>
            {project.adresseChantier && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.adresseChantier}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Indicateurs */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Facturé (HT)
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>
                {formatCurrency(project.totalFacture)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Dépenses (HT)
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF4444' }}>
                {formatCurrency(project.totalDepenses)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Marge Brute
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: marginColor }}>
                {formatCurrency(project.margeBrute)}
              </Typography>
            </Box>
          </Box>

          {/* Taux de marge */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Taux de marge
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {project.tauxMarge >= 30 ? (
                  <TrendingUp sx={{ fontSize: 18, color: '#10B981' }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 18, color: '#EF4444' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 700, color: marginColor }}>
                  {formatPercentage(project.tauxMarge)}
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(Math.max(project.tauxMarge, 0), 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#E5E7EB',
                '& .MuiLinearProgress-bar': {
                  bgcolor: marginColor,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          {/* Compteurs */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<Receipt sx={{ fontSize: 16 }} />}
              label={`${project.nombreFactures} facture${project.nombreFactures > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }}
            />
            <Chip
              icon={<ShoppingCart sx={{ fontSize: 16 }} />}
              label={`${project.nombreDepenses} dépense${project.nombreDepenses > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#FEE2E2', color: '#991B1B' }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Résumé global */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              backgroundColor: `${theme.palette.primary.main}20`,
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Assessment sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Performance par Projet
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Facturé (HT)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#10B981' }}>
                {formatCurrency(totalFacture)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Dépenses (HT)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#EF4444' }}>
                {formatCurrency(totalDepenses)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Marge Brute Globale
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: getMarginColor(tauxMargeGlobal),
                }}
              >
                {formatCurrency(margeBruteGlobale)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Taux de Marge Global
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: getMarginColor(tauxMargeGlobal),
                }}
              >
                {formatPercentage(tauxMargeGlobal)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {projects.length} projet{projects.length > 1 ? 's' : ''} avec données financières
          </Typography>
        </Alert>
      </Paper>

      {/* Vue mobile : Cartes */}
      {isMobile ? (
        <Grid container spacing={2}>
          {projects.map((project) => (
            <Grid item xs={12} key={project.projetId}>
              <ProjectCard project={project} />
            </Grid>
          ))}
        </Grid>
      ) : (
        /* Vue desktop : Tableau + Cartes */
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Projet</TableCell>
                  <TableCell>Adresse</TableCell>
                  <TableCell align="right">Facturé (HT)</TableCell>
                  <TableCell align="right">Dépenses (HT)</TableCell>
                  <TableCell align="right">Marge Brute</TableCell>
                  <TableCell align="right">Taux de Marge</TableCell>
                  <TableCell align="center">Factures</TableCell>
                  <TableCell align="center">Dépenses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => {
                  const marginColor = getMarginColor(project.tauxMarge);
                  return (
                    <TableRow key={project.projetId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {project.projetTitre}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {project.adresseChantier || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>
                          {formatCurrency(project.totalFacture)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF4444' }}>
                          {formatCurrency(project.totalDepenses)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: marginColor }}>
                          {formatCurrency(project.margeBrute)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {project.tauxMarge >= 30 ? (
                            <TrendingUp sx={{ fontSize: 18, color: '#10B981' }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 18, color: '#EF4444' }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 700, color: marginColor }}>
                            {formatPercentage(project.tauxMarge)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={project.nombreFactures}
                          size="small"
                          sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={project.nombreDepenses}
                          size="small"
                          sx={{ bgcolor: '#FEE2E2', color: '#991B1B' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Cartes pour vue détaillée */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Détails par projet
          </Typography>
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.projetId}>
                <ProjectCard project={project} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ProjectMarginsView;
